import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { audienceRejected, AUD } from '../constants/tokenAudience';
import { CLIENT_RELEASE } from '../constants/clientRelease';
import { unregisterDeviceToken } from '../utils/devicePushUnregister';

const AuthContext = createContext(null);

// Same convention as usePushNotifications.js / useAppBadge.js — this file only
// needs it for the logout-time device unregister below; everything else here
// goes through axios interceptors.
const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// 🔑 Dropping this device's push registration on the way out lives in utils/devicePushUnregister (no JSX, so
// backend/test/devicePushUnregister.test.js runs it). Both ways a session ends call it — logout() and the 401
// interceptor — each with the token READ BEFORE it is cleared.
async function dropThisDevicePush(jwt) {
  if (typeof window === 'undefined' || !window.Capacitor?.isNativePlatform?.()) return null;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    unregisterDeviceToken(FirebaseMessaging, jwt, API);
    return FirebaseMessaging;
  } catch (_) { return null; /* non-blocking */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('doorly_token'));
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length !== 3) {
          throw new Error('JWT malformed: not 3 segments');
        }
        const payload = JSON.parse(atob(parts[1]));
        const nowMs = Date.now();
        const expMs = payload.exp * 1000;
        const daysRemaining = Math.round((expMs - nowMs) / 86400000);

        // ── 🚨 THE AUDIENCE CUTOFF, CHECKED BEFORE THE SESSION IS ADOPTED ──
        //
        // backend/constants/tokenAudience.js refuses a token with no `aud` from
        // 2026-11-06. This check only read `exp`, so after that date a borrower
        // holding a pre-audience token would boot into the authenticated app,
        // fire its first request, and be bounced by the 401 interceptor below.
        //
        // 🔑 IT REACHED /login EITHER WAY — the loop was never the risk. The risk
        // is what renders in between: a dashboard mounting several fetches,
        // several rejections, and whatever error state each component paints
        // before window.location.href replaces the page. On a cold native start
        // that is a visible flash of broken app, and it is the first thing a
        // borrower would see after the cutoff.
        //
        // ⚠️ TREATED EXACTLY LIKE AN EXPIRY, because that is what it is — a token
        // this server will no longer accept. Same removal, same clean start at
        // the login screen, no request made and nothing rendered in between.
        //
        // ⚠️ AND IT IS NOT A SECURITY CONTROL. The server decides; this only
        // lets the client reach the same conclusion a moment sooner.
        if (audienceRejected(payload, AUD.BORROWER, nowMs)) {
          console.warn('[Auth] Token audience no longer accepted — removing session.',
            `aud: ${payload.aud || 'none'}`);
          if (window.Sentry) {
            window.Sentry.captureMessage('[Auth] Audience-rejected token cleared at startup', {
              level: 'warning',
              extra: { aud: payload.aud || null, userId: payload.id },
            });
          }
          localStorage.removeItem('doorly_token');
          setToken(null);
        } else if (expMs > nowMs) {
          setUser({
            id: payload.id,
            firstName: payload.firstName,
            lastName: payload.lastName,
            email: payload.email,
            // ⚠️ DISPLAY ONLY — it decides whether a link RENDERS, nothing else.
            // Minted in backend routes/auth.js makeToken() and carried through
            // the sliding renewal in backend middleware/auth.js; see the long
            // note above makeToken for why it is not called isAdmin (that name
            // is already an authorization input in several routes). Anything
            // reached through the link it renders still enforces server-side on
            // the separate admin session. Absent on tokens minted before this
            // shipped, which reads as false — see the re-login note below.
            staffLinkHint: payload.staffLinkHint === true,
          });
        } else {
          // Token expired — log for Sentry visibility
          console.warn(
            '[Auth] Token expired — removing session.',
            `exp: ${new Date(expMs).toISOString()}`,
            `now: ${new Date(nowMs).toISOString()}`,
            `days past expiry: ${Math.abs(daysRemaining)}`
          );
          if (window.Sentry) {
            window.Sentry.captureMessage(
              `[Auth] Token expired for user ${payload?.email || 'unknown'}`,
              {
                level: 'warning',
                extra: {
                  exp: new Date(expMs).toISOString(),
                  now: new Date(nowMs).toISOString(),
                  daysPastExpiry: Math.abs(daysRemaining),
                  userId: payload?.id,
                },
              }
            );
          }
          localStorage.removeItem('doorly_token');
          setToken(null);
        }
      } catch (err) {
        // Token malformed — log for Sentry visibility
        console.warn('[Auth] Token malformed — removing session.', err.message);
        if (window.Sentry) {
          window.Sentry.captureMessage(
            '[Auth] Malformed token cleared from localStorage',
            {
              level: 'warning',
              extra: {
                error: err.message,
                tokenPrefix: token?.slice(0, 20) + '...',
              },
            }
          );
        }
        localStorage.removeItem('doorly_token');
        setToken(null);
      }
    }
    setAuthLoading(false);
  }, [token]);

  // Sliding-session: the backend returns a freshly-minted token in the
  // X-Refresh-Token response header when the current one is within 7 days of
  // expiry. Swap it in silently so active borrowers are never logged out.
  // Registered once on mount; ejected on unmount.
  useEffect(() => {
    // Client-platform telemetry — stamp every backend call with the runtime
    // platform (Capacitor.getPlatform() → 'ios' | 'android' | 'web'), so the
    // backend can measure the borrower base's platform split. Defaults to 'web'
    // if the Capacitor runtime is unavailable. Measurement only — the backend
    // sanitizes this header and never trusts it for auth/eligibility.
    const reqInterceptorId = axios.interceptors.request.use((config) => {
      let platform = 'web';
      try { platform = Capacitor.getPlatform() || 'web'; } catch (_) { platform = 'web'; }
      config.headers = config.headers || {};
      config.headers['X-Client-Platform'] = platform;
      // 🔑 And this bundle's release (constants/clientRelease): an old native bundle is told to update, never dead-ended.
      config.headers['X-Client-Build'] = String(CLIENT_RELEASE);
      return config;
    });

    const interceptorId = axios.interceptors.response.use(
      (response) => {
        const fresh = response.headers?.['x-refresh-token'];
        if (fresh && fresh !== localStorage.getItem('doorly_token')) {
          localStorage.setItem('doorly_token', fresh);
          setToken(fresh);
        }
        return response;
      },
      (error) => {
        // Clean logout on 401 — token rejected by backend (expired, invalid
        // signature, or revoked). Without this, a rejected token leaves the
        // user "logged in" client-side (the session check only reads exp)
        // while every request silently fails.
        if (error.response?.status === 401) {
          // The admin portal has its own auth (doorly_admin_token) and its own
          // 401 handling (→ /admin/login). This borrower-side interceptor must
          // NOT hijack admin 401s — doing so was bouncing logged-in admins to
          // the borrower /login on every admin API 401 (e.g. a missing admin
          // token on one call). Leave admin routes to handle their own 401s.
          if (window.location.pathname.startsWith('/admin')) {
            return Promise.reject(error);
          }
          console.warn('[Auth] 401 received — clearing session');
          // 🔑 THE DEVICE IS DROPPED HERE TOO (2026-11-03). This path cleared the session and left the push row, so a
          // signed-out phone kept receiving the account's notifications. The rejected token is read BEFORE it is
          // cleared and sent with the unregister, which accepts a recently EXPIRED borrower token for that one action
          // (routes/notifications.js). Fired, never awaited.
          const rejected = localStorage.getItem('doorly_token');
          if (rejected) dropThisDevicePush(rejected);
          localStorage.removeItem('doorly_token');
          setToken(null);
          if (!window.location.pathname.includes('/login') &&
              !window.location.pathname.includes('/register')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptorId);
      axios.interceptors.request.eject(reqInterceptorId);
    };
  }, []);

  const login = (newToken, userData) => {
    localStorage.setItem('doorly_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = async () => {
    // Tear down native push listeners on logout (no-op in browser/PWA).
    // Dynamic import so @capacitor-firebase/messaging never loads in the
    // browser bundle path. Non-blocking — never delays/blocks logout.
    // Fired before the token is dropped, because the route needs it — and not awaited, because logout must complete
    // regardless (utils/devicePushUnregister says why both halves matter).
    const FirebaseMessaging = await dropThisDevicePush(token);
    if (FirebaseMessaging) { try { await FirebaseMessaging.removeAllListeners(); } catch (_) { /* non-blocking */ } }
    localStorage.removeItem('doorly_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
